import '../text';
import '../file-picker';
import { defineCustomElement, getAttribute, getBooleanAttribute, getIntegerAttribute, getReactEventHandler, isAttrEqual, isAttrTrue, NectaryElement, setClass, updateAttribute, updateBooleanAttribute } from '../utils';
const templateHTML = '<style>:host{display:block}#wrapper{position:relative;display:flex;flex-direction:row;flex-wrap:wrap;justify-content:center;align-content:center;gap:8px;height:148px;min-width:148px;box-sizing:border-box;padding:16px;border-radius:var(--sinch-comp-file-drop-shape-radius);background-color:var(--sinch-comp-file-drop-color-default-background-initial)}#wrapper::after{content:"";position:absolute;top:0;left:0;right:0;bottom:0;border:1px dashed var(--sinch-comp-file-drop-color-default-border-initial);border-radius:var(--sinch-comp-file-drop-shape-radius);pointer-events:none}#placeholder{align-self:center;text-align:center;--sinch-global-color-text:var(--sinch-comp-file-drop-color-default-placeholder-initial);--sinch-comp-text-font:var(--sinch-comp-file-drop-font-placeholder)}:host([invalid]) #wrapper{background-color:var(--sinch-comp-file-drop-color-invalid-background-initial)}:host([invalid]) #wrapper::after{border-color:var(--sinch-comp-file-drop-color-invalid-border-initial);border-width:1px}#wrapper.drop::after{pointer-events:all}#wrapper.drop.valid{background-color:var(--sinch-comp-file-drop-color-default-background-active)}#wrapper.drop.valid::after{border-color:var(--sinch-comp-file-drop-color-default-border-active);border-width:2px}#wrapper.drop.valid>#placeholder{--sinch-global-color-text:var(--sinch-comp-file-drop-color-default-placeholder-active)}:host([disabled])>#wrapper>#placeholder{--sinch-global-color-text:var(--sinch-comp-file-drop-color-disabled-placeholder-initial)}:host([disabled])>#wrapper{background-color:var(--sinch-comp-file-drop-color-disabled-background-initial)}:host([disabled])>#wrapper::after{border-color:var(--sinch-comp-file-drop-color-disabled-border-initial);border-width:1px}</style><div id="wrapper"><sinch-text id="placeholder" type="m" aria-hidden="true"></sinch-text><sinch-file-picker id="file-picker"><slot></slot></sinch-file-picker></div>';
import { areFilesAccepted, doFilesSatisfySize } from './utils';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-file-drop', class extends NectaryElement {
  #$filePicker;
  #$dropArea;
  #$placeholder;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$dropArea = shadowRoot.querySelector('#wrapper');
    this.#$placeholder = shadowRoot.querySelector('#placeholder');
    this.#$filePicker = shadowRoot.querySelector('#file-picker');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    const options = {
      signal
    };
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    this.addEventListener('-invalid', this.#onInvalidReactHandler, options);
    this.addEventListener('dragenter', this.#onDragEnter, options);
    this.addEventListener('dragleave', this.#onDragLeave, options);
    this.addEventListener('dragover', this.#onDragOver, options);
    this.addEventListener('drop', this.#onDrop, options);
    this.#$filePicker.addEventListener('-change', this.#onFilePickerChange, options);
    this.#$filePicker.addEventListener('-invalid', this.#onFilePickerInvalid, options);
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['accept', 'multiple', 'placeholder', 'disabled', 'invalid', 'size'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'multiple':
        {
          updateAttribute(this.#$filePicker, 'multiple', newVal);
          break;
        }
      case 'accept':
        {
          updateAttribute(this.#$filePicker, 'accept', newVal);
          break;
        }
      case 'placeholder':
        {
          this.#$placeholder.textContent = newVal;
          break;
        }
      case 'disabled':
        {
          this.#setDragEffect(false);
          updateBooleanAttribute(this, 'disabled', isAttrTrue(newVal));
          break;
        }
      case 'invalid':
        {
          updateBooleanAttribute(this, 'invalid', isAttrTrue(newVal));
          break;
        }
      case 'size':
        {
          updateAttribute(this.#$filePicker, 'size', newVal);
          break;
        }
    }
  }
  set multiple(isMultiple) {
    updateBooleanAttribute(this, 'multiple', isMultiple);
  }
  get multiple() {
    return getBooleanAttribute(this, 'multiple');
  }
  set accept(value) {
    updateAttribute(this, 'accept', value);
  }
  get accept() {
    return getAttribute(this, 'accept');
  }
  get size() {
    return getIntegerAttribute(this, 'size', null);
  }
  set size(value) {
    updateAttribute(this, 'size', value);
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  set placeholder(value) {
    updateAttribute(this, 'placeholder', value);
  }
  get placeholder() {
    return getAttribute(this, 'placeholder', '');
  }
  set disabled(isDisabled) {
    updateBooleanAttribute(this, 'disabled', isDisabled);
  }
  get disabled() {
    return getBooleanAttribute(this, 'disabled');
  }
  set invalid(isInvalid) {
    updateBooleanAttribute(this, 'invalid', isInvalid);
  }
  get invalid() {
    return getBooleanAttribute(this, 'invalid');
  }
  #setDragEffect(shouldEnable) {
    let isValid = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    const isElementEnabled = !this.disabled;
    if (shouldEnable) {
      if (isElementEnabled) {
        setClass(this.#$dropArea, 'drop', true);
        setClass(this.#$dropArea, 'valid', isValid);
      }
    } else {
      setClass(this.#$dropArea, 'drop', false);
      setClass(this.#$dropArea, 'valid', false);
    }
  }
  #onDragEnter = e => {
    e.stopPropagation();
    e.preventDefault();
    const hasTypes = e.dataTransfer?.types?.includes('Files') === true;
    const hasItems = e.dataTransfer?.items != null && e.dataTransfer.items.length > 0;
    this.#setDragEffect(true, hasTypes || hasItems);
  };
  #onDragLeave = e => {
    e.stopPropagation();
    e.preventDefault();
    this.#setDragEffect(false);
  };
  #onDragOver = e => {
    e.stopPropagation();
    e.preventDefault();
  };
  #onDrop = e => {
    e.stopPropagation();
    e.preventDefault();
    this.#setDragEffect(false);
    if (this.disabled) {
      return;
    }
    const dt = e.dataTransfer;
    if (dt === null) {
      return;
    }
    if (dt.files.length === 0) {
      return;
    }
    if (!this.multiple && dt.files.length > 1) {
      this.#dispatchInvalidEvent('multiple');
      return;
    }
    const files = Array.from(dt.files);
    if (!areFilesAccepted(files, this.accept)) {
      this.#dispatchInvalidEvent('accept');
      return;
    }
    if (!doFilesSatisfySize(files, this.size)) {
      this.#dispatchInvalidEvent('size');
      return;
    }
    this.#dispatchChangeEvent(files);
  };
  #onFilePickerChange = e => {
    this.#dispatchChangeEvent(e.detail);
  };
  #onFilePickerInvalid = e => {
    this.#dispatchInvalidEvent(e.detail);
  };
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
  #onInvalidReactHandler = e => {
    getReactEventHandler(this, 'on-invalid')?.(e);
  };
  #dispatchChangeEvent(files) {
    this.dispatchEvent(new CustomEvent('-change', {
      detail: files
    }));
  }
  #dispatchInvalidEvent(type) {
    this.dispatchEvent(new CustomEvent('-invalid', {
      detail: type
    }));
  }
});